import {
  FIT_PADDING_RATIO,
  getCanvasViewportDimensions,
} from "@/lib/viewport-dimensions";
import { getViewportInsets } from "@/hooks/use-viewport-insets";
import { focusSurfaceViewport } from "@/lib/focus/focus-surface";
import type { PresentationVariant } from "@/lib/focus/focus-types";

const MARGIN = 24;
export const TOP_MARGIN = 16;
const MIN_W = 320;
const MIN_H = 240;
const DENK_FOCUS_FIT_SCALE = 1 + FIT_PADDING_RATIO * 2;

export interface PresentationBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function calculatePresentationBox(
  viewport: { width: number; height: number },
  insets: { left: number; top: number },
): PresentationBox {
  const x = insets.left + MARGIN;
  const y = insets.top + TOP_MARGIN;
  return {
    x,
    y,
    width: Math.max(MIN_W, viewport.width - x - MARGIN),
    height: Math.max(MIN_H, viewport.height - y - MARGIN),
  };
}

/**
 * The comfortable box review and deliverables show their frame in: on the
 * wall the focus surface the overlay draws in, else the canvas viewport.
 */
export function currentPresentationBox(variant: PresentationVariant) {
  const { viewportWidth, viewportHeight } = focusSurfaceViewport();
  const insets = getViewportInsets();
  return calculatePresentationBox(
    { width: viewportWidth, height: viewportHeight },
    { left: insets.left, top: variant === "review" ? 0 : insets.top },
  );
}

export function calculateDenkFocusBox(
  viewport: { width: number; height: number },
  insets: { left: number; top: number },
): PresentationBox {
  const box = calculatePresentationBox(viewport, insets);
  const width = Math.max(MIN_W, box.width / DENK_FOCUS_FIT_SCALE);
  const height = Math.max(MIN_H, box.height / DENK_FOCUS_FIT_SCALE);
  return {
    x: box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  };
}

export function getCurrentDenkFocusBox(): PresentationBox {
  const { viewportWidth, viewportHeight } = getCanvasViewportDimensions();
  const insets = getViewportInsets();
  return calculateDenkFocusBox(
    { width: viewportWidth, height: viewportHeight },
    { left: insets.left, top: insets.top },
  );
}
